import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import FilterableSkuList, {
  SkuListColumn,
  SkuListFilter,
} from "components/FilterableSkuList";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Navigate, useParams } from "react-router-dom";
import contextStore from "stores/contextStore";

const licensureBodySkuFilters = [
  SkuListFilter.Search,
  SkuListFilter.Dates,
  SkuListFilter.SkuFormat,
  SkuListFilter.SkuAvailability,
  SkuListFilter.SkuStatus,
  SkuListFilter.ApprovalStatus,
];

const LicensureBodySkus = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const { licensureBodyService, licensureBodyDetailsUrl } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const licensureBodyResult = useQuery(
    ["licensureBodyService.getLicensureBodyById", id, organizationId],
    () => licensureBodyService.getLicensureBodyById(id!),
    {
      enabled: !!id,
    },
  );

  const licensureBody = licensureBodyResult.data?.data;
  useTitle(licensureBody?.name || t({ id: "licensureBodies" }));

  const qualificationIds = (licensureBody?.accreditationChain ?? [])
    .map((c) => c.accreditationBodyId)
    .filter((qid): qid is string => !!qid);

  if (!id) {
    return <Navigate to={licensureBodyDetailsUrl(id)} replace />;
  }
  if (licensureBodyResult.isLoading) {
    return <Loader />;
  }
  if (licensureBodyResult.isError || !licensureBody) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="licensureBodyNotAccessible" />
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={licensureBodyDetailsUrl(id)}
        labelKey="backToLicensureBody"
      />
      <h1>
        <u>
          <T id="associatedSkus" />
        </u>
        : {licensureBody.name}
      </h1>
      {qualificationIds.length === 0 ? (
        <Alert severity="info">{t({ id: "accreditationChainEmpty" })}</Alert>
      ) : (
        <FilterableSkuList
          organizationId={organizationId}
          lazyTableTitle="associatedSkus"
          preSelectedQualificationIds={qualificationIds}
          columns={[
            SkuListColumn.Sku,
            SkuListColumn.Title,
            SkuListColumn.SkuFormat,
            SkuListColumn.SkuAvailability,
            SkuListColumn.Status,
          ]}
          filters={licensureBodySkuFilters}
        />
      )}
    </>
  );
};

export default LicensureBodySkus;
